// Subscription utility functions for TradeFix AI (Firestore-backed)
import { getSubscription, updateSubscriptionStatus, getTrades } from './services/firestoreService';

const FREE_TRADE_LIMIT = 10;
const TRIAL_PERIOD_DAYS = 7;

// Get user's current subscription from Firestore
export const getUserSubscription = async (userId) => {
  if (!userId) return null;
  return await getSubscription(userId);
};

// Get the trade limit for a given plan
export const getTradeLimit = (planId) => {
  if (!planId || planId === 'free') return FREE_TRADE_LIMIT;
  return Infinity;
};

// Check if user has an active paid subscription
export const hasActiveSubscription = async (userId) => {
  const sub = await getUserSubscription(userId);
  if (!sub) return false;
  if (sub.status !== 'active') return false;
  if (new Date(sub.endDate) < new Date()) return false;
  return true;
};

// Check if user can add another trade
export const canAddTrade = async (userId) => {
  if (await hasActiveSubscription(userId)) return true;
  const trades = await getTrades(userId);
  return trades.length < FREE_TRADE_LIMIT;
};

// Get remaining trades for free users (returns Infinity for paid)
export const getRemainingTrades = async (userId) => {
  if (await hasActiveSubscription(userId)) return Infinity;
  const trades = await getTrades(userId);
  return Math.max(0, FREE_TRADE_LIMIT - trades.length);
};

// Check if user is within the 7-day trial window from registration
export const isInTrialPeriod = (user) => {
  if (!user || !user.createdAt) return false;
  const createdAt = new Date(user.createdAt);
  const now = new Date();
  const daysDiff = (now - createdAt) / (1000 * 60 * 60 * 24);
  return daysDiff <= TRIAL_PERIOD_DAYS;
};

// Cancel subscription (access continues until end date)
export const cancelSubscription = async (userId) => {
  const sub = await getUserSubscription(userId);

  if (!sub) {
    return { success: false, message: 'No subscription found for your account.' };
  }
  if (sub.status === 'cancelled') {
    return { success: false, message: 'Your subscription is already cancelled.' };
  }

  await updateSubscriptionStatus(userId, {
    status: 'cancelled',
    cancelledAt: new Date().toISOString(),
  });

  const endDate = new Date(sub.endDate).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  return {
    success: true,
    message: `Subscription cancelled successfully. You'll retain full access until ${endDate}. No further charges will be made.`
  };
};
